import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { PAGES } from '../brochurePages';
import { useLang } from '../i18n';

const MIN = 1;
const MAX = 4;

const PageZoomModal = ({ pageIndex, onClose, onNavigate, container }) => {
  const { t } = useLang();
  const [scale, setScale] = useState(1);
  const open = pageIndex !== null;

  const clamp = (v) => Math.min(MAX, Math.max(MIN, v));
  const zoomBy = useCallback((d) => setScale((s) => clamp(+(s + d).toFixed(2))), []);

  useEffect(() => {
    setScale(1);
  }, [pageIndex]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNavigate(Math.min(PAGES.length - 1, pageIndex + 1));
      if (e.key === 'ArrowLeft') onNavigate(Math.max(0, pageIndex - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, pageIndex, onClose, onNavigate]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="zoom-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-[#04090c]/92 backdrop-blur-xl flex flex-col"
          onWheel={(e) => zoomBy(e.deltaY < 0 ? 0.25 : -0.25)}
        >
          <div className="flex items-center justify-between px-5 sm:px-8 h-16 shrink-0 border-b border-[#d4af37]/15">
            <span
              data-testid="zoom-page-label"
              className="font-mono-alt text-[10px] tracking-[0.25em] uppercase text-[#e6c687]"
            >
              {PAGES[pageIndex].label} · {pageIndex + 1} / {PAGES.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                data-testid="zoom-out-btn"
                onClick={() => zoomBy(-0.5)}
                className="w-10 h-10 rounded-full border border-[#d4af37]/30 flex items-center justify-center text-[#e6c687] hover:bg-[#d4af37]/10 transition-colors duration-200"
                aria-label="Riduci zoom"
              >
                <ZoomOut size={16} strokeWidth={1.6} />
              </button>
              <input
                data-testid="zoom-slider"
                type="range"
                min={MIN}
                max={MAX}
                step={0.1}
                value={scale}
                onChange={(e) => setScale(clamp(parseFloat(e.target.value)))}
                className="w-28 accent-[#d4af37]"
                aria-label="Livello di zoom"
              />
              <button
                data-testid="zoom-in-btn"
                onClick={() => zoomBy(0.5)}
                className="w-10 h-10 rounded-full border border-[#d4af37]/30 flex items-center justify-center text-[#e6c687] hover:bg-[#d4af37]/10 transition-colors duration-200"
                aria-label="Aumenta zoom"
              >
                <ZoomIn size={16} strokeWidth={1.6} />
              </button>
              <button
                data-testid="zoom-reset-btn"
                onClick={() => setScale(1)}
                className="w-10 h-10 rounded-full border border-[#d4af37]/30 flex items-center justify-center text-[#e6c687] hover:bg-[#d4af37]/10 transition-colors duration-200"
                aria-label="Reimposta zoom"
              >
                <RotateCcw size={15} strokeWidth={1.6} />
              </button>
              <button
                data-testid="zoom-close-btn"
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-[#d4af37] flex items-center justify-center text-[#070e12] hover:bg-[#f3e5ab] transition-colors duration-200 ml-2"
                aria-label="Chiudi zoom"
              >
                <X size={17} strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="relative flex-1 overflow-hidden flex items-center justify-center">
            <motion.img
              key={pageIndex}
              data-testid="zoom-page-image"
              src={PAGES[pageIndex].src}
              alt={PAGES[pageIndex].alt}
              drag={scale > 1}
              dragMomentum={false}
              dragElastic={0.08}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full max-w-full object-contain select-none rounded-sm shadow-2xl"
              style={{ cursor: scale > 1 ? 'grab' : 'default' }}
              draggable={false}
            />

            <button
              data-testid="zoom-prev-btn"
              onClick={() => onNavigate(Math.max(0, pageIndex - 1))}
              disabled={pageIndex === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[#d4af37]/30 bg-[#0c171c]/70 backdrop-blur flex items-center justify-center text-[#e6c687] hover:bg-[#d4af37]/15 transition-colors duration-200 disabled:opacity-25 disabled:pointer-events-none"
              aria-label="Pagina precedente"
            >
              <ChevronLeft size={20} strokeWidth={1.6} />
            </button>
            <button
              data-testid="zoom-next-btn"
              onClick={() => onNavigate(Math.min(PAGES.length - 1, pageIndex + 1))}
              disabled={pageIndex === PAGES.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-[#d4af37]/30 bg-[#0c171c]/70 backdrop-blur flex items-center justify-center text-[#e6c687] hover:bg-[#d4af37]/15 transition-colors duration-200 disabled:opacity-25 disabled:pointer-events-none"
              aria-label="Pagina successiva"
            >
              <ChevronRight size={20} strokeWidth={1.6} />
            </button>
          </div>

          <p className="shrink-0 text-center pb-4 font-mono-alt text-[9px] tracking-[0.25em] uppercase text-[#a3b1b6]/60">
            {t('zoom.hint')}
          </p>
        </motion.div>
      )}
    </AnimatePresence>,
    container || document.body
  );
};

export default PageZoomModal;
